// Thư mục: src/stores/baoDuongXeStore.js
import { defineStore } from "pinia";
import { BaoDuongXeApi } from "@/services/baoDuongXeService";

export const useBaoDuongXeStore = defineStore("baoDuongXe", {
  state: () => ({
    // State cho danh sách
    isLoadingList: false,
    listError: null,
    items: [], // Danh sách phiếu (phân trang)
    page: 0,
    size: 10,
    sort: "ngayTao,desc",
    totalPages: 1,
    totalElements: 0,
    q: "",
    filterTrangThai: null,

    // State cho modal chi tiết/thực thi
    isLoadingDetail: false,
    detailError: null,
    currentTicket: null,
    giaThamKhao: null, // Luồng mới không cần, để đây nếu sau có dùng

    // State cho options (cache lại)
    isLoadingOptions: false,
    optionsError: null,
    xeOptions: [], // [{id, bienSo, tenLoaiXe}]
    dmBaoDuongOptions: [], // [{id, maHangMuc, tenHangMuc}]
  }),

  actions: {
    // --- Actions Tải Danh sách ---
    setQuery(keyword) {
      this.q = keyword;
      this.page = 0;
    },
    setPage(p1) {
      this.page = Math.max(0, p1 - 1);
      this.fetchList();
    },
    setSize(s) {
      this.size = s;
      this.page = 0;
      this.fetchList();
    },

    async setFilterTrangThai(status) {
      this.filterTrangThai = status; // Cập nhật state
      this.page = 0; // Reset về trang đầu
      await this.fetchList(); // Tải lại danh sách
    },

    async fetchList() {
      this.isLoadingList = true;
      this.listError = null;
      try {
        const data = await BaoDuongXeApi.listAll({
          q: this.q,
          page: this.page,
          size: this.size,
          sort: this.sort,
          trangThai: this.filterTrangThai,
        });
        this.items = data.content || [];
        this.totalPages = data.totalPages ?? 1;
        this.totalElements = data.totalElements ?? this.items.length;
        this.page = data.number ?? 0;
        this.size = data.size ?? this.size;
      } catch (e) {
        this.listError =
          e?.response?.data?.message ||
          e?.message ||
          "Lỗi tải danh sách phiếu bảo dưỡng";
        console.error("fetchList BaoDuongXe error:", e);
      } finally {
        this.isLoadingList = false;
      }
    },

    // --- Actions Tải Dữ liệu cho Modal ---
    async fetchOptionsIfNeeded() {
      if (this.xeOptions.length > 0 && this.dmBaoDuongOptions.length > 0)
        return;
      this.isLoadingOptions = true;
      this.optionsError = null;
      try {
        const [xeData, dmData] = await Promise.all([
          BaoDuongXeApi.getXeOptions(),
          BaoDuongXeApi.getDmBaoDuongOptions(),
        ]);
        this.xeOptions = xeData || [];
        this.dmBaoDuongOptions = dmData || [];
      } catch (e) {
        this.optionsError = "Lỗi tải dữ liệu Xe hoặc Hạng mục";
        console.error("fetchOptionsIfNeeded error:", e);
      } finally {
        this.isLoadingOptions = false;
      }
    },

    async openTicketDetail(ticketId) {
      this.isLoadingDetail = true;
      this.detailError = null;
      this.currentTicket = null;
      this.giaThamKhao = null;
      try {
        this.currentTicket = await BaoDuongXeApi.getById(ticketId);
        // Luồng mới: không cần auto load giaThamKhao ở CHO_DUYET nữa
      } catch (e) {
        this.detailError =
          e?.response?.data?.message || e?.message || "Lỗi tải chi tiết phiếu";
        console.error("openTicketDetail error:", e);
      } finally {
        this.isLoadingDetail = false;
      }
    },

    clearTicketDetail() {
      this.currentTicket = null;
      this.detailError = null;
      this.giaThamKhao = null;
    },

    // --- Actions Quy trình chính ---

    async createTicket(payload) {
      this.isLoadingDetail = true;
      this.detailError = null;
      try {
        await BaoDuongXeApi.create(payload);
        await this.fetchList();
      } catch (e) {
        this.detailError =
          e?.response?.data?.message || e?.message || "Lỗi tạo phiếu mới";
        console.error("createTicket error:", e);
        throw e;
      } finally {
        this.isLoadingDetail = false;
      }
    },

    /**
     * submitGia (luồng mới) = chọn hướng xử lý + giá dự kiến
     * Backend sẽ chuyển từ MỚI -> ĐANG_SỬA
     */
    async submitGia(ticketId, payload) {
      this.isLoadingDetail = true;
      this.detailError = null;
      try {
        const updatedTicket = await BaoDuongXeApi.submitBaoGia(
          ticketId,
          payload
        );
        this.currentTicket = updatedTicket;
        this.giaThamKhao = null;
        await this.fetchList();
      } catch (e) {
        this.detailError =
          e?.response?.data?.message ||
          e?.message ||
          "Lỗi cập nhật hướng xử lý / báo giá";
        console.error("submitGia error:", e);
        throw e;
      } finally {
        this.isLoadingDetail = false;
      }
    },

    // === legacy, luồng cũ. FE mới không cần xài, để lại cho chắc ===
    async approveGia(ticketId) {
      this.isLoadingDetail = true;
      this.detailError = null;
      try {
        const updatedTicket = await BaoDuongXeApi.duyetBaoGia(ticketId);
        this.currentTicket = updatedTicket;
        await this.fetchList();
      } catch (e) {
        this.detailError =
          e?.response?.data?.message || e?.message || "Lỗi duyệt báo giá";
        console.error("approveGia error:", e);
        throw e;
      } finally {
        this.isLoadingDetail = false;
      }
    },

    async startSuaChua(ticketId) {
      this.isLoadingDetail = true;
      this.detailError = null;
      try {
        const updatedTicket = await BaoDuongXeApi.batDauSuaChua(ticketId);
        this.currentTicket = updatedTicket;
        await this.fetchList();
      } catch (e) {
        this.detailError =
          e?.response?.data?.message ||
          e?.message ||
          "Lỗi khi bắt đầu sửa chữa";
        console.error("startSuaChua error:", e);
        throw e;
      } finally {
        this.isLoadingDetail = false;
      }
    },

    async completeSuaChua(ticketId, payload) {
      this.isLoadingDetail = true;
      this.detailError = null;
      try {
        const updatedTicket = await BaoDuongXeApi.hoanThanhSuaChua(
          ticketId,
          payload
        );
        this.currentTicket = updatedTicket;
        await this.fetchList();
      } catch (e) {
        this.detailError =
          e?.response?.data?.message || e?.message || "Lỗi xác nhận hoàn thành";
        console.error("completeSuaChua error:", e);
        throw e;
      } finally {
        this.isLoadingDetail = false;
      }
    },

    async confirmThanhToan(ticketId, ghiChu) {
      this.isLoadingDetail = true;
      this.detailError = null;
      try {
        const updatedTicket = await BaoDuongXeApi.xacNhanThanhToan(
          ticketId,
          ghiChu
        );
        this.currentTicket = updatedTicket;
        await this.fetchList();
      } catch (e) {
        this.detailError =
          e?.response?.data?.message || e?.message || "Lỗi xác nhận thanh toán";
        console.error("confirmThanhToan error:", e);
        throw e;
      } finally {
        this.isLoadingDetail = false;
      }
    },

    async cancelTicket(ticketId, lyDo) {
      this.isLoadingDetail = true;
      this.detailError = null;
      try {
        const updatedTicket = await BaoDuongXeApi.huyPhieu(ticketId, lyDo);
        this.currentTicket = updatedTicket;
        await this.fetchList();
      } catch (e) {
        this.detailError =
          e?.response?.data?.message || e?.message || "Lỗi khi hủy phiếu";
        console.error("cancelTicket error:", e);
        throw e;
      } finally {
        this.isLoadingDetail = false;
      }
    },

    async createTicketFromSos(payload) {
      this.isLoadingDetail = true;
      this.detailError = null;
      try {
        const newTicket = await BaoDuongXeApi.createFromSos(payload);
        this.currentTicket = newTicket;
        return newTicket;
      } catch (e) {
        this.detailError =
          e?.response?.data?.message || e?.message || "Lỗi gửi báo cáo sự cố";
        console.error("createTicketFromSos error:", e);
        throw e;
      } finally {
        this.isLoadingDetail = false;
      }
    },

    async updateTrangThai(id, newStatus) {
      this.isLoadingDetail = true;
      this.detailError = null;
      try {
        const res = await BaoDuongXeApi.chuyenTrangThai(id, newStatus);
        this.currentTicket = res;
        await this.fetchList();
      } catch (e) {
        this.detailError =
          e?.response?.data?.message || e?.message || "Lỗi chuyển trạng thái";
        console.error("updateTrangThai error:", e);
        throw e;
      } finally {
        this.isLoadingDetail = false;
      }
    },
  },
});
